import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash } from "@phosphor-icons/react";

export default function CategoriesPage() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", description: "" });

  const load = async () => { const { data } = await api.get("/categories"); setItems(data); };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editing) await api.put(`/categories/${editing.id}`, form);
      else await api.post("/categories", form);
      toast.success(editing ? "Categoria aggiornata" : "Categoria creata");
      setOpen(false); setForm({ name: "", description: "" }); setEditing(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Eliminare la categoria "${c.name}"?`)) return;
    await api.delete(`/categories/${c.id}`); toast.success("Categoria eliminata"); load();
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); setForm({ name: "", description: "" }); } }}>
          <DialogTrigger asChild>
            <Button data-testid="new-category-btn" className="h-10 bg-slate-900 hover:bg-slate-800 text-white">
              <Plus weight="bold" size={16} className="mr-1.5"/> Nuova categoria
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white">
            <DialogHeader><DialogTitle className="font-display tracking-tight">{editing ? "Modifica categoria" : "Nuova categoria"}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-600">Nome *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="category-name-input"/>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-600">Descrizione</Label>
                <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1" data-testid="category-description-input"/>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
              <Button onClick={save} data-testid="category-save-btn" className="bg-slate-900 hover:bg-slate-800 text-white">Salva</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border-slate-200 rounded-md shadow-none overflow-hidden" data-testid="categories-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-widest text-slate-500 bg-slate-50">
              <th className="px-5 py-3 font-medium">Nome</th>
              <th className="px-5 py-3 font-medium">Descrizione</th>
              <th className="px-5 py-3 font-medium text-right">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={3} className="px-5 py-10 text-center text-slate-500">Nessuna categoria.</td></tr>}
            {items.map((c) => (
              <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-5 py-3 font-medium">{c.name}</td>
                <td className="px-5 py-3 text-slate-600">{c.description || "—"}</td>
                <td className="px-5 py-3 text-right">
                  <button className="w-8 h-8 rounded-md hover:bg-slate-100 text-slate-600 inline-grid place-items-center" onClick={() => { setEditing(c); setForm({ name: c.name, description: c.description }); setOpen(true); }} data-testid={`edit-cat-${c.name}`}><PencilSimple size={16}/></button>
                  <button className="w-8 h-8 rounded-md hover:bg-red-50 text-red-600 inline-grid place-items-center" onClick={() => remove(c)} data-testid={`delete-cat-${c.name}`}><Trash size={16}/></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
